import React, { useState } from 'react';
import axios from 'axios';
import API_CONFIG from '../../config/api';

const ImageUpload = ({ value, onChange, label, className }) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Reset error
        setError(null);

        // Client-side validation
        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            setError('File size must be less than 5MB');
            return;
        }

        const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        if (!validTypes.includes(file.type)) {
            setError('Invalid file type. Only JPG, PNG, WEBP, and GIF are allowed.');
            return;
        }

        try {
            setUploading(true);

            // 1. Get Presigned URL
            const token = localStorage.getItem('impera_admin_token');
            const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

            const presignResponse = await axios.post(
                `${API_CONFIG.BASE_URL}/upload/presigned-url`,
                { filename: file.name },
                { headers: authHeader }
            );

            if (!presignResponse.data.success) {
                throw new Error(presignResponse.data.message || 'Failed to get upload URL');
            }

            const { presignedUrl, publicUrl } = presignResponse.data;

            // 2. Upload to MinIO
            // Note: No headers needed for the PUT to MinIO signed URL usually, 
            // but Content-Type should match what was signed? 
            // The minio SDK presignedPutObject usually doesn't strictly enforce content-type signature unless specified.
            // But let's send it to be safe.
            await axios.put(presignedUrl, file, {
                headers: {
                    'Content-Type': file.type
                }
            });

            // 3. Update parent with the public URL
            onChange(publicUrl);

        } catch (err) {
            console.error('Upload failed:', err);
            setError(err.message || 'Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className={`space-y-2 ${className}`}>
            <label className="block text-sm font-medium text-gray-400 mb-1">{label}</label>
            <div className="flex items-center gap-4">
                {/* Preview */}
                <div className="relative w-24 h-24 bg-black/30 rounded-lg border border-white/10 overflow-hidden flex-shrink-0 flex items-center justify-center group">
                    {value ? (
                        <>
                            <img src={value} alt="Preview" className="w-full h-full object-cover" />
                            {/* Remove button */}
                            <button
                                type="button"
                                onClick={() => onChange('')}
                                className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white text-xs"
                            >
                                Remove
                            </button>
                        </>
                    ) : (
                        <span className="text-gray-500 text-xs">No Image</span>
                    )}
                </div>

                {/* Input Area */}
                <div className="flex-1">
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        disabled={uploading}
                        className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple file:text-white hover:file:bg-purple/80 cursor-pointer disabled:opacity-50"
                    />
                    {uploading && <p className="text-xs text-blue-400 mt-1">Uploading...</p>}
                    {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
                </div>
            </div>

        </div>
    );
};

export default ImageUpload;
